/*
  Price-range-slider: visuell dual-handle slider för S&D price-filter.

  Synkar med Dawn:s två text-inputs (Från/Till) inom samma <price-range>.
  - Slider input (drag) → uppdaterar text-inputsen live med värdena
  - Slider change (release) → dispatchar 'input'-event på text-input vilket
    bubblar till facet-formen → Dawn:s debouncedOnSubmit filtrerar. Bound-
    värden (0 / max) rensas så inga no-op-params submittas.
  - Text-input change → flyttar slider-handles

  Custom element så det re-initialiseras automatiskt när FacetFiltersForm
  re-renderar facets-sektionen efter varje filtrering.
*/

(function () {
  // Locale-robust money-parse. Hanterar "5 999,00", "5.999,00", "5999.00",
  // "5999". Sista separatorn med <=2 siffror efter tolkas som decimal,
  // övriga separatorer = tusental. Annars är alla separatorer tusental.
  function parseMoney(str) {
    if (str === null || str === undefined || str === '') return null;
    const s = String(str).replace(/[^\d.,]/g, '');
    if (!s) return null;

    const lastSep = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    const decimals = lastSep === -1 ? 0 : s.length - lastSep - 1;

    let n;
    if (lastSep !== -1 && decimals >= 1 && decimals <= 2) {
      const intPart = s.slice(0, lastSep).replace(/[.,]/g, '');
      const decPart = s.slice(lastSep + 1);
      n = parseFloat(intPart + '.' + decPart);
    } else {
      n = parseFloat(s.replace(/[.,]/g, ''));
    }
    return isNaN(n) ? null : n;
  }

  class PriceRangeSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.minHandle = this.querySelector('[data-slider-min]');
      this.maxHandle = this.querySelector('[data-slider-max]');
      this.fill = this.querySelector('[data-slider-fill]');
      if (!this.minHandle || !this.maxHandle) return;

      const priceRange = this.closest('price-range');
      if (!priceRange) return;
      const textInputs = priceRange.querySelectorAll('input[inputmode="decimal"]');
      this.minInput = textInputs[0];
      this.maxInput = textInputs[1];
      if (!this.minInput || !this.maxInput) return;

      this.max = parseMoney(this.minInput.getAttribute('data-max'));
      if (!this.max || this.max <= 0) return;
      this.ceil = Math.ceil(this.max);

      [this.minHandle, this.maxHandle].forEach((h) => {
        h.min = 0;
        h.max = this.ceil;
        h.step = 1;
        // Dawn:s PriceRange-element lägger change-listeners på ALLA inputs
        // inuti <price-range>, inkl dessa range-sliders. Dess adjustToValidValues
        // läser data-max → utan attributet blir Number(null)=0 och värdet
        // klampas till 0 (handle hoppar vänster). Sätt attributen så clampen
        // blir harmlös (value ligger alltid inom [0, ceil]).
        h.setAttribute('data-min', '0');
        h.setAttribute('data-max', this.ceil);
      });

      // Initiala värden: från aktiva filter-params (server-renderade i input-
      // value), annars bounds. Vi för-fyller INTE rutorna vid full range -
      // då skulle de ifyllda värdena serialiseras med varje filter-submit och
      // ge en evig "0 - max"-pris-pill. Tomma rutor visar placeholdern (0/max)
      // och submittas inte. onSlide fyller i siffrorna live vid drag.
      const startMin = parseMoney(this.minInput.value);
      const startMax = parseMoney(this.maxInput.value);
      this.minHandle.value = startMin === null ? 0 : startMin;
      this.maxHandle.value = startMax === null ? this.ceil : startMax;

      this.minHandle.addEventListener('input', this.onSlide.bind(this));
      this.maxHandle.addEventListener('input', this.onSlide.bind(this));
      this.minHandle.addEventListener('change', this.onCommit.bind(this));
      this.maxHandle.addEventListener('change', this.onCommit.bind(this));
      this.minInput.addEventListener('change', this.syncFromInputs.bind(this));
      this.maxInput.addEventListener('change', this.syncFromInputs.bind(this));

      this.updateFill();
    }

    onSlide(event) {
      let minVal = Number(this.minHandle.value);
      let maxVal = Number(this.maxHandle.value);

      // Förhindra att handles korsar varandra
      if (minVal > maxVal) {
        if (event.target === this.minHandle) {
          minVal = maxVal;
          this.minHandle.value = minVal;
        } else {
          maxVal = minVal;
          this.maxHandle.value = maxVal;
        }
      }

      // Visa live-värdena i rutorna (utan att filtrera än)
      this.minInput.value = Math.round(minVal);
      this.maxInput.value = Math.round(maxVal);
      this.updateFill();
    }

    onCommit() {
      const minVal = Number(this.minHandle.value);
      const maxVal = Number(this.maxHandle.value);

      // Rensa BÅDA bara om hela intervallet är valt (= inget filter) så inga
      // no-op-params submittas. Vid partiellt filter behåll båda siffrorna
      // synliga i rutorna (annars försvinner 0 / maxpris ur rutan).
      if (minVal <= 0 && maxVal >= this.ceil) {
        this.minInput.value = '';
        this.maxInput.value = '';
      } else {
        this.minInput.value = Math.round(minVal);
        this.maxInput.value = Math.round(maxVal);
      }

      // ENDAST input-event (triggar form-filtrering). INTE change - det triggar
      // Dawn:s PriceRange.onRangeChange som klampar tomma text-inputs mot den
      // andra handlens värde. Native change på range-inputen sköter Dawn:s
      // data-attr-synk redan.
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromInputs() {
      const minVal = parseMoney(this.minInput.value);
      const maxVal = parseMoney(this.maxInput.value);
      this.minHandle.value = minVal === null ? 0 : minVal;
      this.maxHandle.value = maxVal === null ? this.ceil : maxVal;
      this.updateFill();
    }

    updateFill() {
      if (!this.fill) return;
      const minPct = (Number(this.minHandle.value) / this.ceil) * 100;
      const maxPct = (Number(this.maxHandle.value) / this.ceil) * 100;
      this.fill.style.left = minPct + '%';
      this.fill.style.right = 100 - maxPct + '%';
    }
  }

  if (!customElements.get('price-range-slider')) {
    customElements.define('price-range-slider', PriceRangeSlider);
  }
})();
